import { polygonContains } from 'd3';
import type { Polygon as GeoJsonPolygon } from 'geojson';
import polylabel from 'polylabel';

import type { CoordinateTransformer, Point } from '~types';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type SvgPoint = [number, number];

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

/**
 * Default precision for polylabel in geographic units.
 * This is precise enough for SVG label placement without over-refining the search.
 */
const DEFAULT_LABEL_PRECISION = 0.01;

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

/**
 * Convert an array of geographic coordinates to SVG points using the provided transformer.
 * @param coords - Array of [lon, lat] points
 * @param transformer - Coordinate transformer with a toSVG method
 * @returns Array of [x, y] SVG points
 */
const toSvgPoints = (coords: Point[], transformer: CoordinateTransformer): SvgPoint[] =>
  coords.map(([lon, lat]) => {
    const { x, y } = transformer.toSVG(lon, lat);
    return [x, y];
  });

/**
 * Calculate the bounding box of a set of SVG points.
 * @param points - Array of [x, y] SVG points
 * @returns Object with minX, maxX, minY, maxY properties
 */
const getBoundingBox = (points: SvgPoint[]): { minX: number; maxX: number; minY: number; maxY: number } => {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const [x, y] of points) {
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }

  return { minX, maxX, minY, maxY };
};

/**
 * Calculate the optimal label point for a polygon using the pole-of-inaccessibility algorithm (polylabel).
 * This finds the point inside the polygon that is furthest from all edges, ideal for irregular/concave shapes.
 * @param coords - Array of [lon, lat] points (exterior ring)
 * @param precision - Precision parameter in geographic units; smaller = more accurate but slower
 * @returns [lon, lat] point inside the polygon, or null if calculation fails
 */
const getPolylabelPoint = (coords: Point[], precision: number): Point | null => {
  if (coords.length < 3) return null;

  try {
    // polylabel expects [[[lon, lat], [lon, lat], ...]]
    const geoJsonPolygon = [coords];
    const [lon, lat] = polylabel(geoJsonPolygon, precision);
    return [lon, lat];
  } catch {
    return null;
  }
};

/**
 * Find the nearest interior point to the polygon's bounding-box centre by sampling a grid.
 * Used as a fallback when polylabel fails to produce a valid result.
 * @param polygon - Array of [x, y] points defining the polygon vertices
 * @param bounds - Bounding box of the polygon with minX, maxX, minY, maxY
 * @returns [x, y] point inside the polygon closest to the bounding-box centre, or null if none found
 */
const findNearestInsideSample = (
  polygon: SvgPoint[],
  bounds: { minX: number; maxX: number; minY: number; maxY: number },
): SvgPoint | null => {
  const { minX, maxX, minY, maxY } = bounds;
  const width = maxX - minX;
  const height = maxY - minY;
  const referencePoint: SvgPoint = [(minX + maxX) / 2, (minY + maxY) / 2];

  if (width === 0 && height === 0) {
    // All polygon points collapsed to one SVG coordinate; return it directly.
    return [minX, minY];
  }

  const columns = 16;
  const rows = 16;
  const stepX = width / (columns + 1);
  const stepY = height / (rows + 1);

  let bestPoint: SvgPoint | null = null;
  let bestDistance = Infinity;

  for (let col = 1; col <= columns; col += 1) {
    for (let row = 1; row <= rows; row += 1) {
      const point: SvgPoint = [minX + stepX * col, minY + stepY * row];
      if (!polygonContains(polygon, point)) continue;

      const dx = point[0] - referencePoint[0];
      const dy = point[1] - referencePoint[1];
      const distance = Math.hypot(dx, dy);

      if (distance < bestDistance) {
        bestDistance = distance;
        bestPoint = point;
      }
    }
  }

  return bestPoint;
};

/**
 * Find a fallback center point for a polygon when all other methods fail.
 * This simply returns the midpoint of the bounding box, which may not be ideal but ensures a point is returned.
 * @param bounds - Bounding box of the polygon with minX, maxX, minY, maxY
 * @returns [x, y] point at the center of the bounding box
 */
const findFallbackCenter = (bounds: { minX: number; maxX: number; minY: number; maxY: number }): SvgPoint => [
  (bounds.minX + bounds.maxX) / 2,
  (bounds.minY + bounds.maxY) / 2,
];

/* -------------------------------------------------------------------------- */
/*                                  Utilities                                 */
/* -------------------------------------------------------------------------- */

/**
 * Serialize an array of geographic coordinates into an SVG `points` attribute string.
 * @param coords - Array of [lon, lat] points
 * @param transformer - Coordinate transformer
 * @returns An SVG-compatible points string (e.g. "x1,y1 x2,y2 ...")
 */
const coordsToSVGPoints = (coords: Point[], transformer: CoordinateTransformer): string =>
  toSvgPoints(coords, transformer)
    .map(([x, y]) => `${x},${y}`)
    .join(' ');

/**
 * Convert a GeoJSON Polygon's exterior ring into an SVG `points` attribute string.
 * Extracts the first coordinate ring, converts positions to [lon, lat] tuples,
 * and transforms them into SVG coordinates.
 * @param geometry - GeoJSON Polygon geometry
 * @param transformer - Coordinate transformer
 * @returns An SVG-compatible points string, or null if the geometry has no valid coordinates
 */
const polygonToSVGPoints = (geometry: GeoJsonPolygon, transformer: CoordinateTransformer): string | null => {
  const coordinates = geometry.coordinates[0];
  if (!coordinates?.length) return null;

  const coords: Point[] = coordinates.map((pos) => [pos[0], pos[1]] as Point);
  return coordsToSVGPoints(coords, transformer);
};

/**
 * Calculate a robust label point for a polygon in SVG coordinates.
 * Uses the pole-of-inaccessibility algorithm (polylabel) for irregular/concave shapes.
 * Falls back to grid sampling, then bounding box center if polylabel fails.
 * @param coords - Array of [lon, lat] points (exterior ring)
 * @param transformer - Coordinate transformer
 * @param precision - Precision in geographic units (degrees for WGS84); default is 0.01.
 *                    Smaller values are more accurate but slower.
 * @returns Label point { x, y } or null if coords are empty
 */
const polygonCenterToSVG = (
  coords: Point[],
  transformer: CoordinateTransformer,
  precision: number = DEFAULT_LABEL_PRECISION,
): { x: number; y: number } | null => {
  if (!coords.length) return null;

  // Try polylabel first (best for irregular mining blast polygons)
  const polylabelPoint = getPolylabelPoint(coords, precision);
  if (polylabelPoint) {
    const { x, y } = transformer.toSVG(polylabelPoint[0], polylabelPoint[1]);
    return { x, y };
  }

  // Fallback to grid sampling if polylabel fails
  const polygon = toSvgPoints(coords, transformer);
  if (polygon.length === 1) return { x: polygon[0][0], y: polygon[0][1] };
  if (polygon.length === 2) {
    return { x: (polygon[0][0] + polygon[1][0]) / 2, y: (polygon[0][1] + polygon[1][1]) / 2 };
  }

  const bounds = getBoundingBox(polygon);

  const sampledPoint = findNearestInsideSample(polygon, bounds);
  if (sampledPoint) return { x: sampledPoint[0], y: sampledPoint[1] };

  const fallback = findFallbackCenter(bounds);
  return { x: fallback[0], y: fallback[1] };
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { coordsToSVGPoints, polygonCenterToSVG, polygonToSVGPoints };
